import {
  Alert,
  Autocomplete,
  CircularProgress,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Checkbox,
  Button,
  TextField,
  Typography,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import MarkEmailUnreadIcon from "@mui/icons-material/MarkEmailUnread";
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import React from "react";
import { AddInstrumentModalController, AddInstrumentModalControllerDelegate, IAssignment } from "./controller";

const SUCCESS_CLOSE_DELAY_MS = 2500;

interface State {
  isLoading: boolean;
  phase: "form" | "submitting" | "success";
  errorMessage: string;
  assignmentsError: string;
  assignmentsLoading: boolean;
  assignmentsInput: string;
  assignments: IAssignment[];
  selectedAssignments: IAssignment[];
  errors: {
    insufficientLicenses: boolean;
  }
  /** Delivery outcome of the notification; `null` = none was owed. */
  emailDelivered: boolean | null;
}

interface Props {
  userUid?: string;
  delegate: AddInstrumentModalDelegate;
  /**
   * Whether the target already finished registration. It decides whether adding
   * applications e-mails anything at all: the backend issues access codes — and
   * mails them — only for someone with a pending invitation. `undefined` means
   * the caller could not tell, and the copy stays conditional rather than
   * asserting either outcome.
   */
  targetHasAccount?: boolean;
}

export interface AddInstrumentModalDelegate {
  shouldCloseModal: () => void;
  applicationShouldLogout: () => void;
  didAddAssignments: () => void;
}

class AddInstrumentModal extends React.Component<Props, State> implements AddInstrumentModalControllerDelegate {

  controller: AddInstrumentModalController
  private assignmentsFetchDebounce?: NodeJS.Timeout;
  private closeTimer?: NodeJS.Timeout;

  constructor(props: Props) {
    super(props);
    this.state = {
      isLoading: true,
      phase: "form",
      errorMessage: "",
      assignmentsError: "",
      assignmentsLoading: false,
      assignmentsInput: "",
      assignments: [],
      selectedAssignments: [],
      errors: {
        insufficientLicenses: false
      },
      emailDelivered: null,
    }
    this.controller = new AddInstrumentModalController(this);
  }

  // delegate
  didGetAssignments(assignments: IAssignment[]) {
    this.setState({
      assignments,
      assignmentsError: "",
    });
  }

  didFailGetAssignments(message: string) {
    this.setState({ assignmentsError: message });
  }

  insufficientLicensesError() {
    this.setState({
      isLoading: false,
      phase: "form",
      errors: {
        insufficientLicenses: true
      }
    })
  }

  didAddAssignments(emailDelivered: boolean | null) {
    this.setState({ phase: "success", errorMessage: "", emailDelivered });
    // Only a confirmed send closes itself. `false` is a warning and `null` asks
    // the psychologist to verify the list — both would be lost in 2.5s.
    if (emailDelivered !== true) {
      return;
    }
    this.closeTimer = setTimeout(
      () => this.props.delegate.didAddAssignments(),
      SUCCESS_CLOSE_DELAY_MS
    );
  }

  didFailAddAssignments(message: string) {
    this.setState({ phase: "form", errorMessage: message });
  }

  applicationShouldLogout() {
    this.setState({ phase: "form" });
    this.props.delegate.applicationShouldLogout();
  };

  // internals

  handleChangeSelected(selectedAssignments: IAssignment[]): void {
    this.setState({
      selectedAssignments
    })
  }

  componentDidMount(): void {
    this.fetchAssignments("");
  }

  componentWillUnmount(): void {
    clearTimeout(this.assignmentsFetchDebounce!);
    clearTimeout(this.closeTimer!);
  }

  didClickClose() {
    if (this.state.phase === "submitting") {
      return;
    }
    if (this.state.phase === "success") {
      clearTimeout(this.closeTimer!);
      this.props.delegate.didAddAssignments();
      return;
    }
    this.props.delegate.shouldCloseModal();
  }

  didClickAddInstruments() {
    this.setState({
      phase: "submitting",
      errorMessage: "",
    });
    const token = localStorage.getItem("AccessToken")!;
    const userUid = this.props.userUid!;
    const idsToAdd = this.state.selectedAssignments.map(
      (assignment) => assignment.applicationId
    );
    this.controller.addAssignmentsToUser(token, userUid, idsToAdd);
  }

  private fetchAssignments(filter: string) {
    this.setState({ assignmentsLoading: true });
    clearTimeout(this.assignmentsFetchDebounce!);
    this.assignmentsFetchDebounce = setTimeout(async () => {
      try {
        const token = localStorage.getItem("AccessToken")!;
        const normalizedFilter = filter.trim();
        const params = {
          page: 1,
          limit: 15,
          ...(normalizedFilter ? { filter: normalizedFilter } : {}),
        };
        await this.controller.getManagedAssignments(token, {
          ...params,
        });
      } finally {
        this.setState({ assignmentsLoading: false, isLoading: false });
      }
    }, 300);
  }

  renderLoading() {
    return (
      <Box
        alignItems="center"
        display="flex"
        flexDirection="column"
        marginTop={2}
      >
        <CircularProgress />
      </Box>
    );
  }

  renderSubmitting() {
    return (
      <Box
        alignItems="center"
        display="flex"
        flexDirection="column"
        justifyContent="center"
        gap={2}
        paddingY={5}
      >
        <CircularProgress />
        <Typography variant="body2" color="text.secondary">
          Adicionando aplicações...
        </Typography>
      </Box>
    );
  }

  renderSuccess() {
    const total = this.state.selectedAssignments.length;

    if (this.state.emailDelivered === false) {
      return (
        <Box
          alignItems="center"
          display="flex"
          flexDirection="column"
          justifyContent="center"
          gap={1.5}
          paddingY={4}
        >
          <MarkEmailUnreadIcon sx={{ fontSize: 56, color: "warning.main" }} />
          <Typography
            variant="subtitle1"
            color="warning.main"
            fontWeight="bold"
          >
            {total === 1
              ? "Aplicação adicionada, e-mail não enviado"
              : "Aplicações adicionadas, e-mail não enviado"}
          </Typography>
          <Typography variant="body2" color="text.secondary" align="center">
            O vínculo e as licenças foram registrados — apenas o e-mail de aviso
            não saiu. Use "Enviar acesso por e-mail" no menu da aplicação para
            reenviar.
          </Typography>
          <Button
            variant="contained"
            onClick={() => this.didClickClose()}
            sx={{ mt: 1 }}
          >
            Entendi
          </Button>
        </Box>
      );
    }

    // `null` is a real answer from this route, not just a missing body: no
    // e-mail was OWED. Two causes reach it and the response cannot tell them
    // apart — nothing was inserted (every selection was already open for this
    // person), or there is nobody to notify (anonymous, or an indeterminate
    // state). Dressing that as "Aplicação adicionada!" would claim an effect
    // that may not have happened, so it asks for a look instead.
    if (this.state.emailDelivered === null) {
      return (
        <Box
          alignItems="center"
          display="flex"
          flexDirection="column"
          justifyContent="center"
          gap={1.5}
          paddingY={4}
        >
          <InfoOutlinedIcon sx={{ fontSize: 56, color: "info.main" }} />
          <Typography variant="subtitle1" color="info.main" fontWeight="bold">
            Concluído, sem e-mail enviado
          </Typography>
          <Typography variant="body2" color="text.secondary" align="center">
            Nenhum e-mail era devido:{" "}
            {total === 1
              ? "o instrumento selecionado já estava aberto para o usuário"
              : "os instrumentos selecionados já estavam abertos para o usuário"}
            , ou não há endereço para notificar. Confira a lista de aplicações do
            usuário.
          </Typography>
          <Button
            variant="contained"
            onClick={() => this.didClickClose()}
            sx={{ mt: 1 }}
          >
            Entendi
          </Button>
        </Box>
      );
    }

    return (
      <Box
        alignItems="center"
        display="flex"
        flexDirection="column"
        justifyContent="center"
        gap={1}
        paddingY={5}
      >
        <CheckCircleIcon sx={{ fontSize: 56, color: "success.main" }} />
        <Typography variant="subtitle1" color="success.main" fontWeight="bold">
          {total === 1 ? "Aplicação adicionada!" : "Aplicações adicionadas!"}
        </Typography>
        <Typography variant="body2" color="text.secondary" align="center">
          {total === 1
            ? "A aplicação já está disponível para o usuário."
            : `As ${total} aplicações já estão disponíveis para o usuário.`}
        </Typography>
      </Box>
    );
  }

  /**
   * Which e-mail the person on the other end will receive. Since the invite
   * consolidation BOTH states are notified — the difference is the content:
   * a pending invitation gets the access codes, a real account gets a notice
   * listing the instruments. Saying "no e-mail" here, as this used to, would
   * now be plainly wrong.
   *
   * Not an absolute promise: an application the person already had open is
   * skipped, and skipping everything owes no e-mail. `emailDelivered` on the
   * response is what reports the truth after the fact.
   */
  renderEmailNotice() {
    const total = this.state.selectedAssignments.length;
    const codes =
      total === 1 ? "o código de acesso" : `os ${total} códigos de acesso`;

    if (this.props.targetHasAccount === true) {
      return (
        <Alert severity="info">
          <strong>Um e-mail será enviado</strong> avisando{" "}
          {total === 1
            ? "do instrumento adicionado"
            : `dos ${total} instrumentos adicionados`}
          . O usuário já possui conta e entra com e-mail e senha.
        </Alert>
      );
    }

    if (this.props.targetHasAccount === false) {
      return (
        <Alert severity="info">
          <strong>Um e-mail será enviado</strong> com {codes}, para o usuário
          responder sem finalizar o cadastro.
        </Alert>
      );
    }

    return (
      <Alert severity="info">
        <strong>Um e-mail será enviado.</strong> Se o usuário ainda não finalizou
        o cadastro, recebe {codes}; se já possui conta, recebe um aviso dos
        instrumentos adicionados.
      </Alert>
    );
  }

  renderForm() {
    const assignmentsById = new Map<number, IAssignment>();
    this.state.selectedAssignments.forEach((assignment) => {
      if (assignment?.applicationId !== undefined) {
        assignmentsById.set(assignment.applicationId, assignment);
      }
    });
    this.state.assignments.forEach((assignment) => {
      if (assignment?.applicationId !== undefined) {
        assignmentsById.set(assignment.applicationId, assignment);
      }
    });
    const assignmentOptions = Array.from(assignmentsById.values());

    return (
      <Box display="flex" flexDirection="column" sx={{ gap: 2 }}>
        <Typography color="text.secondary">
          Selecione as aplicações que deseja adicionar ao usuário.
        </Typography>

        {this.state.selectedAssignments.length > 0 && this.renderEmailNotice()}

        {this.state.errorMessage && (
          <Alert
            severity="error"
            onClose={() => this.setState({ errorMessage: "" })}
          >
            {this.state.errorMessage}
          </Alert>
        )}

        {this.state.assignmentsError && (
          <Alert
            severity="warning"
            action={
              <Button
                color="inherit"
                size="small"
                onClick={() => this.fetchAssignments(this.state.assignmentsInput)}
              >
                Tentar novamente
              </Button>
            }
          >
            {this.state.assignmentsError}
          </Alert>
        )}

        <Autocomplete
          multiple
          options={assignmentOptions}
          getOptionLabel={(assignment: IAssignment) =>
            `${assignment.title} (${assignment.instrumentTitle})`
          }
          value={this.state.selectedAssignments}
          onChange={(_, newValue) => this.handleChangeSelected(newValue)}
          inputValue={this.state.assignmentsInput}
          onInputChange={(_, value) => {
            this.setState({ assignmentsInput: value }, () => {
              this.fetchAssignments(value);
            });
          }}
          loading={this.state.assignmentsLoading}
          disableCloseOnSelect
          isOptionEqualToValue={(option, value) =>
            option.applicationId === value.applicationId
          }
          renderOption={(props, option, { selected }) => {
            const { key, ...optionProps } = props;
            const optionKey = option.applicationId ?? key;
            return (
              <li key={optionKey} {...optionProps}>
                <Checkbox checked={selected} />
                {`${option.title} (${option.instrumentTitle})`}
              </li>
            );
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Aplicações"
              helperText="Comece a digitar para buscar as aplicações desejadas."
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {this.state.assignmentsLoading && (
                      <CircularProgress color="inherit" size={20} />
                    )}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          ListboxProps={{
            style: {
              maxHeight: 200,
            },
          }}
          noOptionsText="Nenhuma aplicação encontrada"
          sx={{
            width: "100%",
          }}
        />

      </Box>
    );
  }

  renderInssuficientLicenses() {
    return (
      <Box display="flex" flexDirection="column">
        <Typography color="error" align="center" alignSelf="center" fontWeight="bold">ERRO</Typography>
        <Typography color="error" alignSelf="center">Você não possui licenças disponíveis para todos os instrumentos selecionados e por isso não pode atribuir as aplicações.</Typography>
        <Button
          variant="outlined"
          color="error"
          onClick={() => {
            this.props.delegate.shouldCloseModal()
          }}
        >
          Voltar
        </Button>
      </Box>
    )
  }

  // Internals
  render() {
    let dataToRender;
    if (this.state.isLoading) {
      dataToRender = this.renderLoading()
    } else if (this.state.phase === "submitting") {
      dataToRender = this.renderSubmitting()
    } else if (this.state.phase === "success") {
      dataToRender = this.renderSuccess()
    } else if (this.state.errors.insufficientLicenses) {
      dataToRender = this.renderInssuficientLicenses()
    } else {
      dataToRender = this.renderForm()
    }

    const showActions =
      !this.state.isLoading &&
      this.state.phase === "form" &&
      !this.state.errors.insufficientLicenses;

    return (
      <Dialog
        open={!!this.props.userUid}
        onClose={() => this.didClickClose()}
        variant="modal"
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle variant="modal">
          <NoteAddIcon/>
          Adicionar aplicações
        </DialogTitle>
        <DialogContent variant="modal">
          {dataToRender}
        </DialogContent>
        {showActions && (
          <DialogActions
            variant="modal"
            sx={{ flexWrap: "wrap", gap: 1, justifyContent: "end" }}
          >
            <Button
              variant="outlined"
              color="error"
              onClick={() => {
                this.props.delegate.shouldCloseModal()
              }}
            >
              Voltar
            </Button>

            <Button
              variant="contained"
              onClick={() => this.didClickAddInstruments()}
              disabled={this.state.selectedAssignments.length === 0}
            >
              Adicionar Aplicações
            </Button>
          </DialogActions>
        )}
      </Dialog>
    );
  }
}

export default AddInstrumentModal;
